// Jobs from the last week, used as context on the Map for future days: the
// sites a dispatcher's trucks have recently loaded and dumped at.
import { useEffect, useState } from 'react';

import { limit, orderBy, query, where } from 'firelordjs';

import { compact } from 'lodash';

import { firestore } from '@isd/lib/firebase';
import { Companies, Jobs } from '@isd/lib/firebase/db/collections';
import { getReaderCompaniesConstraint } from '@isd/lib/firebase/db/helpers';
import {
  DispatchScheduleDoc,
  JobDoc,
  UserDoc,
} from '@isd/lib/firebase/db/metaTypes';

import { getStartOfDay, subDays } from '@isd/lib/helpers/dates';
import log from '@isd/lib/logger';

import {
  applyDispatchScheduleParams,
  getHasDispatchScheduleParams,
} from '@/components/Job/useScheduledJobs';

export const RECENT_JOBS_DAYS = 7;
// Job documents are large; with "All Jobs" a week can be thousands of them, so
// only the most recent ones are read
export const RECENT_JOBS_LIMIT = 1000;

type RecentJobsResult = { jobs: JobDoc[]; isCapped: boolean };

// One read per user, schedule and day for the whole page load
const recentJobsRequests: Record<string, Promise<RecentJobsResult>> = {};

/**
 * The Dispatch Schedule parameters the last-week read depends on, as a key, so
 * editing a schedule's clients, sites, radius or vehicle type reads again
 * instead of reusing the cached jobs for its old parameters.
 * @param {DispatchScheduleDoc | null} dispatchScheduleDoc - Null for "All Jobs".
 * @returns {string}
 */
function getDispatchScheduleParamsKey(
  dispatchScheduleDoc: DispatchScheduleDoc | null
): string {
  if (!dispatchScheduleDoc) return 'all';
  const {
    clientsIds,
    sitesIds,
    radiusGeoPoint,
    radiusDistanceMiles,
    radiusZipCode,
    vehicleType,
  } = dispatchScheduleDoc.data() || {};
  return JSON.stringify([
    dispatchScheduleDoc.id,
    [...(clientsIds || [])].sort(),
    [...(sitesIds || [])].sort(),
    radiusGeoPoint ? [radiusGeoPoint.latitude, radiusGeoPoint.longitude] : null,
    radiusDistanceMiles ?? null,
    radiusZipCode ?? null,
    vehicleType ?? null,
  ]);
}

/**
 * Read the jobs of the last week for the selected Dispatch Schedule, newest
 * first, applying the schedule's parameters.
 * @param {UserDoc} userDoc
 * @param {DispatchScheduleDoc | null} dispatchScheduleDoc - Null for "All Jobs".
 * @returns {Promise<RecentJobsResult>}
 */
function loadRecentJobs(
  userDoc: UserDoc,
  dispatchScheduleDoc: DispatchScheduleDoc | null
): Promise<RecentJobsResult> {
  const now = new Date();
  const since = getStartOfDay(subDays(now, RECENT_JOBS_DAYS));
  const key = [
    userDoc.id,
    getDispatchScheduleParamsKey(dispatchScheduleDoc),
    since.getTime(),
  ].join(':');
  if (!getHasDispatchScheduleParams(dispatchScheduleDoc)) {
    return Promise.resolve({ jobs: [], isCapped: false });
  }
  const clientsIds = dispatchScheduleDoc?.get('clientsIds') || [];
  recentJobsRequests[key] =
    recentJobsRequests[key] ||
    firestore
      // Include soft-deleted docs so the cap check counts what the query returned
      .getDocsIncludeDeleted(
        query(
          Jobs.collection(),
          ...compact([
            getReaderCompaniesConstraint(userDoc),
            !!clientsIds.length &&
              where(
                'client',
                'in',
                clientsIds.map((clientId) => Companies.doc(clientId))
              ),
            where('startDatetime', '>=', since),
            where('startDatetime', '<=', now),
            orderBy('startDatetime', 'desc'),
            limit(RECENT_JOBS_LIMIT),
          ])
        )
      )
      .then((querySnapshot) => ({
        jobs: applyDispatchScheduleParams(
          firestore.filterSoftDeletedDocs(querySnapshot.docs) as JobDoc[],
          dispatchScheduleDoc
        ),
        isCapped: querySnapshot.docs.length >= RECENT_JOBS_LIMIT,
      }))
      .catch((error) => {
        log.error('Could not load the last week of jobs', error);
        // Don't keep a failed read, so the next visit retries
        delete recentJobsRequests[key];
        return { jobs: [], isCapped: false };
      });
  return recentJobsRequests[key];
}

/**
 * Jobs from the last week for the selected Dispatch Schedule, read once.
 * @param {object} params
 * @param {UserDoc | null} params.userDoc
 * @param {DispatchScheduleDoc | null} params.dispatchScheduleDoc
 * @param {boolean} params.enabled - Only read when the context is shown.
 * @returns {{ jobs: JobDoc[], isCapped: boolean, isLoading: boolean }}
 */
export function useRecentJobs({
  userDoc,
  dispatchScheduleDoc,
  enabled,
}: {
  userDoc: UserDoc | null;
  dispatchScheduleDoc: DispatchScheduleDoc | null;
  enabled: boolean;
}): RecentJobsResult & { isLoading: boolean } {
  const [result, setResult] = useState<RecentJobsResult | null>(null);
  const paramsKey = getDispatchScheduleParamsKey(dispatchScheduleDoc);
  useEffect(() => {
    if (!enabled || !userDoc) return;
    let isCancelled = false;
    setResult(null);
    loadRecentJobs(userDoc, dispatchScheduleDoc).then((loaded) => {
      if (!isCancelled) setResult(loaded);
    });
    return () => {
      isCancelled = true;
    };
  }, [enabled, userDoc?.id, paramsKey]);
  return {
    jobs: enabled ? result?.jobs || [] : [],
    isCapped: !!enabled && !!result?.isCapped,
    isLoading: enabled && !result,
  };
}
